import { useMemo } from "react";
import type { TableColumn } from "@/components/common/CommonTable";
import type { IPageInfo } from "@/interface/common.interface";
import type { IUser, EmployeeCategory } from "@/interface/employeeOnBoard";
import type {
  IAttendanceSummaryUser,
  ISalaryPreviewError,
  ISalaryPreviewUser,
} from "@/interface/attendance.interface";
import { API_URL } from "@/api/constant";
import { handleFormatAmount } from "@/utility/helper";
import { Button } from "@/components/ui/button";
import { PlusCircle, X, Download, Loader2 } from "lucide-react";
// import { useNavigate } from "react-router-dom";
// import { RoutesLink } from "@/utility/RouteLinks";

const empty = "-";
// const navigate = useNavigate();



const getRowNumber = (pageInfo: IPageInfo, index: number) =>
  String((pageInfo.currentPage - 1) * pageInfo.pageSize + index + 1).padStart(
    2,
    "0",
  );


const AmountPill = ({
  value,
  tone,
}: {
  value: number;
  tone: "earning" | "deduction" | "net" | "neutral" | "gross";
}) => {
  const toneClass = {
    earning: "bg-emerald-50 text-emerald-700 border-emerald-200",
    deduction: "bg-rose-50 text-rose-700 border-rose-200",
    net: "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200",
    neutral: "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border bg-rose-50 text-rose-700 border-rose-200",
    gross: "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border bg-green-50 text-green-700 border-green-200",
  }[tone];

  return (
    <span
      className={`font-bold text-xs whitespace-nowrap px-2 py-0.5 rounded border ${toneClass}`}
    >
      {handleFormatAmount(value)}
    </span>
  );
};

const getHeadAmount = (
  salaries?: { salaryName: string; amount: string | number | null }[],
  nameKeywords: string[] = [],
): number | null => {
  if (!salaries || !Array.isArray(salaries)) return null;
  const target = salaries.find((s) => {
    const lower = (s.salaryName || "").toLowerCase().trim();
    return nameKeywords.some((kw) => lower.includes(kw) || lower === kw);
  });
  if (
    !target ||
    target.amount === null ||
    target.amount === undefined ||
    target.amount === ""
  )
    return null;
  const num = Number(target.amount);
  return isNaN(num) ? null : num;
};

export const useEmployeeColumns = (
  paginationData: IPageInfo,
  selectedCategory: EmployeeCategory,
): TableColumn<IUser>[] =>
  useMemo(
    () => [
      {
        key: "sNo",
        header: "S.No",
        render: (_item, index) => (
          <span className="text-muted-foreground font-medium text-xs">
            {getRowNumber(paginationData, index)}
          </span>
        ),
      },
      {
        key: "employeeCode",
        header: "Emp Code",
        render: (item) => (
          <span className="font-semibold text-slate-700 text-xs">
            {item.employeeCode || empty}
          </span>
        ),
      },
      {
        key: "name",
        header: "Employee Name",
        render: (item) => (
          <Button className="font-bold text-slate-900 text-xs whitespace-nowrap">
            {item.name || empty}
          </Button>
        ),
      },
      {
        key: "department",
        header: "Department",
        render: (item) => (
          <span className="text-slate-700 text-xs font-medium">
            {item.department?.name || empty}
          </span>
        ),
      },
      {
        key: "designation",
        header: "Designation",
        render: (item) => (
          <span className="text-slate-600 text-xs font-medium">
            {item.designation?.name || empty}
          </span>
        ),
      },
      {
        key: "skillType",
        header: "Category",
        render: (item) => (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
            {item.skillType || selectedCategory}
          </span>
        ),
      },
      {
        key: "salaryConfigration",
        header: "Salary Configration",
        render: (item) => (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${item.isSalaryCreated
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-rose-50 text-rose-700 border-rose-200"
              }`}
          >
            {item.isSalaryCreated ? "Configured" : "Not Configured"}
          </span>
        ),
      },
      {
        key: "grossSalary",
        header: "Gross Salary",
        render: (item) => {
          const grossAmt = getHeadAmount(item.salaries, [
            "gross salary",
            "gross-salary",
            "gross_salary",
            "gross pay",
            "gross",
          ]);
          if (grossAmt === null) {
            return <span className="text-slate-400 text-xs font-medium">-</span>;
          }
          return <AmountPill value={grossAmt} tone="gross" />;
        },
      },
      {
        key: "netSalary",
        header: "Net Salary",
        render: (item) => {
          const netAmt = getHeadAmount(item.salaries, [
            "net salary",
            "net-salary",
            "net_salary",
            "net pay",
          ]);
          if (netAmt === null) {
            return <span className="text-slate-400 text-xs font-medium">-</span>;
          }
          return <AmountPill value={netAmt} tone="net" />;
        },
      },
      {
        key: "status",
        header: "Status",
        render: (item) => {
          const isCurrent = item.status?.toLowerCase() === "current";
          return (
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${isCurrent
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-rose-50 text-rose-700 border-rose-200"
                }`}
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${isCurrent ? "bg-emerald-500" : "bg-rose-500"
                  }`}
              />
              {item.status
                ? item.status.charAt(0).toUpperCase() + item.status.slice(1)
                : "Current"}
            </span>
          );
        },
      },
    ],
    [paginationData, selectedCategory],
  );

export const useAttendanceColumns = (
  attendancePaginationData: IPageInfo,
  onDownloadSalary?: (userId: string) => void,
  downloadingSalaryId?: string | null,
): TableColumn<IAttendanceSummaryUser>[] =>
  useMemo<TableColumn<IAttendanceSummaryUser>[]>(
    () => [
      {
        key: "sNo",
        header: "S.No",
        render: (_item, index) => (
          <span className="text-slate-500 font-medium text-xs">
            {getRowNumber(attendancePaginationData, index)}
          </span>
        ),
      },
      {
        key: "empid",
        header: "Emp ID",
        render: (item) => (
          <span className="font-semibold text-slate-700 text-xs">
            {item.user?.employeeCode || empty}
          </span>
        ),
      },
      {
        key: "name",
        header: "Employee",
        render: (item) => (
          <div className="flex items-center gap-2.5 min-w-[210px]">
            {item.user?.profilePhoto ? (
              <img
                src={`${API_URL}/${item.user.profilePhoto}`}
                alt={item.user.name}
                className="h-8 w-8 min-w-8 rounded-full object-cover shadow-xs border border-slate-200"
              />
            ) : (
              <div className="h-8 w-8 min-w-8 rounded-full bg-primary/10 text-primary font-bold text-xs flex items-center justify-center shadow-xs">
                {item.user?.name?.charAt(0)?.toUpperCase() || "E"}
              </div>
            )}
            <div className="min-w-0">
              <div className="font-bold text-slate-800 text-xs truncate">
                {/* <span className="text-base font-bold"> */}
                {item.user?.name || empty}

                {/* </span> */}
              </div>
              <div className="text-[11px] text-slate-500 truncate">
                {item.user?.email || empty}
              </div>
            </div>
          </div>
        ),
      },
      {
        key: "department",
        header: "Department",
        render: (item) => (
          <span className="text-xs font-medium text-slate-700">
            {item.department || empty}
          </span>
        ),
      },
      {
        key: "shift",
        header: "Shift",
        render: (item) => (
          <span className="text-xs font-medium text-slate-600">
            {item.shift || empty}
          </span>
        ),
      },
      {
        key: "attendanceLock",
        header: "Attendance Lock",
        render: (item) => (
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${item.attendanceLock?.isLocked
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-rose-50 text-rose-700 border-rose-200"
                }`}
            >
              <span
                className={`h-1 w-1  rounded-full ${item.attendanceLock?.isLocked ? "bg-emerald-500" : " bg-rose-500"
                  }`}
              />
              {item.attendanceLock?.isLocked ? "YES" : "No"}
            </span>

          </div>
        ),
      },
      {
        key: "salaryCreate",
        header: "Salary",
        render: (item) => (
          <div className="flex items-center gap-2.5">

            <span
              className={`font-semibold text-xs ${item.salaryCreated?.isCreated
                ? "text-emerald-600"
                : "text-rose-600"
                }`}
            >
              {item.salaryCreated?.isCreated ? "Salary Created" : "Salary Not Created"}
            </span>
            {item.salaryCreated?.isCreated && onDownloadSalary && (
              <Button
                size="sm"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (item.user?.id) {
                    onDownloadSalary(item.user.id);
                  }
                }}
                disabled={downloadingSalaryId === item.user?.id}
                className="group cursor-pointer"
                title="Download Salary Slip"
              >
                <span className="flex items-center">
                  {downloadingSalaryId === item.user?.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Download className="w-3.5 h-3.5 group-hover:animate-bounce" />
                  )}
                  <span>
                    {downloadingSalaryId === item.user?.id ? "" : ""}
                  </span>
                </span>
              </Button>
            )}
          </div>
        ),
      },
      ...[
        ["present", "Present", "text-emerald-600 "],
        ["pending", "Pending ", "text-red-600"],
        ["leaves", "Leave", "text-blue-600"],
        ["wfh", "WFH", "text-indigo-600"],
        ["outdoorDuty", "Outdoor Duty", "text-purple-600"],
        ["absent", "Absent", "text-rose-600"],
        ["holiday", "Holiday", "text-slate-600"],
        ["weekOff", "Week Off", "text-slate-600"],
      ].map(([summaryKey, header, className]) => ({
        key: summaryKey,
        header,
        render: (item: IAttendanceSummaryUser) => (
          <span className={`font-semibold text-xs ${className}`}>
            {item.summary?.[summaryKey as keyof IAttendanceSummaryUser["summary"]] ?? 0}
          </span>
        ),
      })),
      ...[
        ["pendingAr", "Pending Ar"],
        ["pendingLeave", "Pending Leave"],
        ["pendingOutdoorDuty", "Pending OD"],
        ["pendingWfh", "Pending WFH"],
      ].map(([summaryKey, header]) => ({
        key: summaryKey,
        header,
        render: (item: IAttendanceSummaryUser) => {
          const value =
            item.summary?.[
            summaryKey as keyof IAttendanceSummaryUser["summary"]
            ] ?? 0;
          const numericValue = Number(value) || 0;
          return (
            <span
              className={`font-semibold text-xs ${numericValue > 0 ? "text-rose-600" : "text-emerald-600"
                }`}
            >
              {numericValue}
            </span>
          );
        },
      })),
      {
        key: "totalDays",
        header: "Total Days",
        render: (item) => (
          <span className="font-medium text-slate-600 text-xs">
            {item.totalDays ?? 0}
          </span>
        ),
      },

    ],
    [attendancePaginationData, onDownloadSalary, downloadingSalaryId],
  );

export const useSalaryPreviewColumns = (
  salaryPreviewPaginationData: IPageInfo,
  onAddSalaryHeadClick?: (user: ISalaryPreviewUser) => void,
  onRemoveSalaryHeadClick?: (userId: string, salaryHeadId: string) => void,
  salaryPreviewUsers?: ISalaryPreviewUser[],
) => {
  const hasCustomAddedHeads = useMemo(() => {
    if (!salaryPreviewUsers) return false;
    return salaryPreviewUsers.some((u) =>
      u.userSalary?.some(
        (h) => h.isCustomAdded === true && Number(h.amount) > 0,
      ),
    );
  }, [salaryPreviewUsers]);

  const validUsersColumns = useMemo<TableColumn<ISalaryPreviewUser>[]>(() => {
    const cols: TableColumn<ISalaryPreviewUser>[] = [
      {
        key: "sNo",
        header: "S.No",
        render: (_item, index) => (
          <span className="text-muted-foreground font-medium text-xs">
            {getRowNumber(salaryPreviewPaginationData, index)}
          </span>
        ),
      },
      {
        key: "employeeCode",
        header: "Emp Code",
        render: (item) => (
          <span className="font-semibold text-slate-700 text-xs">
            {item.user?.employeeCode || empty}
          </span>
        ),
      },
      {
        key: "name",
        header: "Employee Name",
        render: (item) => (
          <span className="font-bold text-slate-900 text-xs whitespace-nowrap">
            {item.user?.name || empty}
          </span>
        ),
      },
      {
        key: "totalDays",
        header: "Cycle Days",
        render: (item) => (
          <span className="font-semibold text-slate-800 text-xs">
            {item.attendanceSummary?.totalDays ?? 0} Days
          </span>
        ),
      },
      {
        key: "presentDays",
        header: "Present / Half",
        render: (item) => {
          const present = item.attendanceSummary?.attendance?.present ?? 0;
          const halfDay = item.attendanceSummary?.attendance?.halfDay ?? 0;
          return (
            <div className="text-xs flex items-center gap-1.5 whitespace-nowrap">
              <span className="font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">
                {present} Present
              </span>
              <span
                className={`font-semibold px-1.5 py-0.5 rounded border ${halfDay > 0
                  ? "bg-amber-50 text-amber-700 border-amber-100"
                  : "bg-slate-50 text-slate-400 border-slate-200"
                  }`}
              >
                {halfDay} Half
              </span>
            </div>
          );
        },
      },
      {
        key: "absentDays",
        header: "Absent Days",
        render: (item) => {
          const absent = item.attendanceSummary?.absentDays ?? 0;
          return (
            <span
              className={`font-bold text-xs px-2 py-0.5 rounded-full border ${absent > 0
                ? "bg-rose-50 text-rose-600 border-rose-100"
                : "bg-slate-50 text-slate-400 border-slate-200"
                }`}
            >
              {absent} Absent
            </span>
          );
        },
      },
      {
        key: "leaveDays",
        header: "Leaves (Paid / Unpaid)",
        render: (item) => {
          const paid = item.attendanceSummary?.paidLeaveDays ?? 0;
          const unpaid = item.attendanceSummary?.unpaidLeaveDays ?? 0;
          return (
            <div className="text-xs flex items-center gap-1.5 whitespace-nowrap">
              <span className="font-medium px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-100">
                Paid: {paid}
              </span>
              <span
                className={`font-medium px-1.5 py-0.5 rounded border ${unpaid > 0
                  ? "bg-rose-50 text-rose-700 border-rose-100"
                  : "bg-slate-50 text-slate-400 border-slate-200"
                  }`}
              >
                Unpaid: {unpaid}
              </span>
            </div>
          );
        },
      },
      {
        key: "dailyRate",
        header: "Daily Rate",
        render: (item) => (
          <span className="font-semibold text-slate-800 text-xs whitespace-nowrap">
            {handleFormatAmount(item.attendanceSummary?.dailyRate || 0)}
          </span>
        ),
      },
      {
        key: "deductionPolicies",
        header: "Deduction Rules",
        render: (item) => {
          const absentApplied =
            item.attendanceSummary?.isAbsentDeductionApplied ?? false;
          const leaveApplied =
            item.attendanceSummary?.isLeaveDeductionApplied ?? false;
          return (
            <div className="flex flex-col gap-1 text-[11px]">
              {[
                ["Absent", absentApplied],
                ["Leave", leaveApplied],
              ].map(([label, applied]) => (
                <div
                  key={label as string}
                  className="flex items-center gap-1.5 whitespace-nowrap"
                >
                  <span className="text-slate-500 font-medium">
                    {label as string}:
                  </span>
                  <span
                    className={`font-extrabold px-1.5 py-0.5 rounded border ${applied
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-slate-50 text-slate-400 border-slate-200"
                      }`}
                  >
                    {applied ? "Applied" : "Not Applied"}
                  </span>
                </div>
              ))}
            </div>
          );
        },
      },
      {
        key: "absentDeductionCalc",
        header: "Absent Deduction (Rate x Days)",
        render: (item) => {
          const rate = item.attendanceSummary?.dailyRate || 0;
          const days =
            item.attendanceSummary?.absentDeductionDays ??
            item.attendanceSummary?.absentDays ??
            0;
          const amount =
            item.attendanceSummary?.absentDeductionAmount ?? rate * days;
          return <Calculation amount={amount} days={days} rate={rate} />;
        },
      },
      {
        key: "leaveDeductionCalc",
        header: "Leave Deduction (Rate x Days)",
        render: (item) => {
          const rate = item.attendanceSummary?.dailyRate || 0;
          const days =
            item.attendanceSummary?.leaveDeductionDays ??
            item.attendanceSummary?.unpaidLeaveDays ??
            0;
          const amount =
            item.attendanceSummary?.leaveDeductionAmount ?? rate * days;
          return <Calculation amount={amount} days={days} rate={rate} />;
        },
      },
      {
        key: "totalAttDeduction",
        header: "Total Att. Deduction",
        render: (item) => {
          const amount =
            item.attendanceSummary?.totalAttendanceDeductionAmount ??
            (item.attendanceSummary?.absentDeductionAmount || 0) +
            (item.attendanceSummary?.leaveDeductionAmount || 0);
          return (
            <AmountPill
              value={amount}
              tone={amount > 0 ? "deduction" : "neutral"}
            />
          );
        },
      },
      {
        key: "grossSalary",
        header: "Gross Earning",
        render: (item) => (
          <AmountPill value={item.totals?.earning || 0} tone="earning" />
        ),
      },
      {
        key: "deductions",
        header: "Total Deductions",
        render: (item) => (
          <AmountPill value={item.totals?.deduction || 0} tone="deduction" />
        ),
      },
      {
        key: "netSalary",
        header: "Net Salary",
        render: (item) => (
          <AmountPill value={item.totals?.netSalary || 0} tone="net" />
        ),
      },
    ];

    if (hasCustomAddedHeads) {
      cols.push({
        key: "userSalaryHeads",
        header: "Custom / Added Heads",
        render: (item) => {
          const customHeads = (item.userSalary || []).filter(
            (h) => h.isCustomAdded === true && Number(h.amount) > 0,
          );

          if (customHeads.length === 0) {
            return <span className="text-slate-400 text-xs font-medium">-</span>;
          }

          return (
            <div className="flex flex-wrap gap-1.5 max-w-[240px]">
              {customHeads.map((h) => {
                const headId = h.branchSalaryHeadId || h.id;
                const isEarn = (h.type || "").toLowerCase() === "earning";
                const badgeClass = isEarn
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-rose-50 text-rose-700 border-rose-200";

                return (
                  <span
                    key={headId}
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg border ${badgeClass}`}
                  >
                    <span>
                      {h.salaryName || h.name}: ₹{h.amount}
                    </span>
                    {onRemoveSalaryHeadClick && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (item.user?.id && headId) {
                            onRemoveSalaryHeadClick(item.user.id, headId);
                          }
                        }}
                        className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-slate-200/80 hover:bg-rose-500 hover:text-white text-slate-600 ml-1 transition-colors cursor-pointer shrink-0"
                        title="Remove custom head"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </span>
                );
              })}
            </div>
          );
        },
      });
    }

    cols.push({
      key: "actions",
      header: "Actions",
      render: (item) => (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => onAddSalaryHeadClick?.(item)}
          className="h-7 text-xs font-semibold flex items-center gap-1.5 border-blue-200 text-blue-700 bg-blue-50/50 hover:bg-blue-100 hover:text-blue-800 shrink-0 whitespace-nowrap"
        >
          <PlusCircle className="w-3.5 h-3.5" />
          <span>Add Head</span>
        </Button>
      ),
    });

    return cols;
  }, [
    salaryPreviewPaginationData,
    onAddSalaryHeadClick,
    onRemoveSalaryHeadClick,
    hasCustomAddedHeads,
  ]);

  const errorUsersColumns = useMemo<TableColumn<ISalaryPreviewError>[]>(
    () => [
      {
        key: "sNo",
        header: "S.No",
        render: (_item, index) => (
          <span className="text-muted-foreground font-medium text-xs">
            {String(index + 1).padStart(2, "0")}
          </span>
        ),
      },
      {
        key: "employeeCode",
        header: "Emp Code",
        render: (item) => (
          <span className="font-semibold text-slate-700 text-xs">
            {item.user?.employeeCode || empty}
          </span>
        ),
      },
      {
        key: "name",
        header: "Employee Name",
        render: (item) => (
          <span className="font-bold text-slate-900 text-xs whitespace-nowrap">
            {item.user?.name || empty}
          </span>
        ),
      },
      {
        key: "email",
        header: "Email",
        render: (item) => (
          <span className="text-slate-600 text-xs">
            {item.user?.email || empty}
          </span>
        ),
      },
      {
        key: "errorMessage",
        header: "Configuration Error",
        render: (item) => {
          const message =
            item.error?.response?.message ||
            item.error?.message ||
            "Initial salary configuration missing";
          return (
            <span className="font-semibold text-xs text-rose-600 bg-rose-50 px-2.5 py-1 rounded-lg border border-rose-100 whitespace-normal block max-w-md break-words">
              Warning: {message}
            </span>
          );
        },
      },
    ],
    [],
  );

  return { validUsersColumns, errorUsersColumns };
};

const Calculation = ({
  rate,
  days,
  amount,
}: {
  rate: number;
  days: number;
  amount: number;
}) => (
  <div className="text-xs whitespace-nowrap">
    <div className="flex items-center gap-1 text-[11px] text-slate-500 font-medium">
      <span>{handleFormatAmount(rate)}</span>
      <span>x</span>
      <span className="font-bold text-slate-700">{days}d</span>
      <span>=</span>
      <span className={`font-bold ${amount > 0 ? "text-rose-600" : "text-slate-500"}`}>
        {handleFormatAmount(amount)}
      </span>
    </div>
  </div>
);
